import React, { useImperativeHandle } from 'react'
import { useTranslation } from 'react-i18next'
import { FormProvider } from 'react-hook-form'
import { View } from 'react-native'
import Button from '@ui/buttons/Button'
import {
  CofidisSectionConfig,
  cofidisMonthlyApplicationSections,
} from './sections'
import useCofidisApplicationForm from './useCofidisApplicationForm'
import { CofidisApplicationForm } from './useCofidisApplicationForm'
import {
  CofidisApplicationFormPayload,
  CofidisApplicationFormPayloadSchema,
} from '@ca/common'
import { DevFillSectionButton } from './DevFillSectionButton'
import type { CofidisMonthlyApplicationFormValues } from './types'

export type CofidisMonthlyApplicationLayoutProps = {
  form: CofidisApplicationForm
  sections: CofidisSectionConfig[]
  onSubmit: () => void
}

const DefaultLayout = ({
  sections,
  onSubmit,
}: CofidisMonthlyApplicationLayoutProps) => {
  const { t } = useTranslation(['ui'])
  return (
    <View className="gap-8">
      {sections.map((section) => {
        const SectionComponent = section.Component
        return (
          <View key={section.name} className="relative">
            <SectionComponent />
            <DevFillSectionButton section={section} />
          </View>
        )
      })}
      <View className="flex flex-row items-center justify-center p-16">
        <Button
          label={t('ui:submit')}
          onPress={onSubmit}
          iconEnd="arrow-right"
        />
      </View>
    </View>
  )
}

export type CofidisMonthlyApplicationFormProps = {
  /** Form values are sent as-is; backend schema coerces dates, opt-ins, salary. */
  onSubmit?: (values: CofidisApplicationFormPayload) => void | Promise<void>
  /** Optional partial initial form state; merged with built-in defaults. */
  defaultValues?: Partial<CofidisMonthlyApplicationFormValues>
  LayoutComponent?: React.ComponentType<CofidisMonthlyApplicationLayoutProps>
  formRef?: React.RefObject<CofidisApplicationForm | null>
}

export default function CofidisMonthlyApplicationForm({
  onSubmit,
  defaultValues,
  LayoutComponent = DefaultLayout,
  formRef,
}: CofidisMonthlyApplicationFormProps) {
  const form = useCofidisApplicationForm({ defaultValues })
  const sections = cofidisMonthlyApplicationSections

  const submitHandler = form.handleSubmit(async (values) => {
    const payload = CofidisApplicationFormPayloadSchema.safeParse(values)
    if (!payload.success) {
      form.setError('root', { type: 'submit', message: 'submission.failed' })
      return
    }
    await onSubmit?.(payload.data)
  })

  useImperativeHandle(formRef, () => form)

  return (
    <FormProvider {...form}>
      <LayoutComponent
        form={form}
        sections={sections}
        onSubmit={submitHandler}
      />
    </FormProvider>
  )
}
